import React, { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  Row, 
  Col, 
  Avatar, 
  Typography, 
  Tag, 
  Button, 
  Divider, 
  Timeline, 
  Image, 
  Space, 
  Spin
} from 'antd';
import { 
  UserOutlined, 
  ClockCircleOutlined, 
  CodeOutlined,
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  GithubOutlined,
  TwitterOutlined,
  LinkedinOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { MenteeDetailResponse } from '../../types';
import { menteeDetailQueryKeys } from '../../lib/queryClient';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

const { Title, Text, Paragraph } = Typography;

const MenteeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCompany = user?.role === 'Company';

  const menteeListPath = useMemo(() => {
    if (user?.role === 'Mentor') return '/profile?tab=mentees';
    if (user?.role === 'Company') return '/mentees';
    if (user?.role === 'Admin') return '/admin/panel';
    return '/';
  }, [user?.role]);
  const menteeId = useMemo(() => {
    if (id == null || id === '' || !/^\d+$/.test(id)) return null;
    const n = parseInt(id, 10);
    return n > 0 && Number.isSafeInteger(n) ? n : null;
  }, [id]);

  dayjs.locale('tr');

  const safeMenteeId = menteeId ?? 0;
  const { data: menteeDetail, isLoading: loading, isError } = useQuery({
    queryKey: menteeDetailQueryKeys.detail(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeDetail(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const { data: skillsData, isLoading: skillsLoading } = useQuery({
    queryKey: menteeDetailQueryKeys.skills(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeSkillsById(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const { data: educationsData, isLoading: educationsLoading } = useQuery({
    queryKey: menteeDetailQueryKeys.educations(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeEducationsById(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const { data: workExperiencesData, isLoading: workExperiencesLoading } = useQuery({
    queryKey: menteeDetailQueryKeys.workExperiences(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeWorkExperiencesById(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: menteeDetailQueryKeys.projects(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeProjectsById(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const { data: certificatesData, isLoading: certificatesLoading } = useQuery({
    queryKey: menteeDetailQueryKeys.certificates(safeMenteeId),
    queryFn: async () => {
      const response = await apiService.getMenteeCertificatesById(menteeId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: menteeId != null,
    staleTime: 0,
  });

  const mentee = useMemo((): MenteeDetailResponse | null => {
    if (!menteeDetail) return null;
    return {
      ...menteeDetail,
      skills: skillsData ?? menteeDetail.skills ?? [],
      educations: educationsData ?? menteeDetail.educations ?? [],
      workExperiences: workExperiencesData ?? menteeDetail.workExperiences ?? [],
      projects: projectsData ?? menteeDetail.projects ?? [],
      certificates: certificatesData ?? menteeDetail.certificates ?? [],
    };
  }, [menteeDetail, skillsData, educationsData, workExperiencesData, projectsData, certificatesData]);

  useEffect(() => {
    if (!isError) return;
    navigate(menteeListPath, { replace: true });
  }, [isError, menteeListPath, navigate]);

  if (isError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderHeader = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8} md={6}>
            <div style={{ textAlign: 'center' }}>
              {mentee.profileImage ? (
                <img 
                  src={`data:image/jpeg;base64,${mentee.profileImage}`} 
                  alt={`${mentee.firstName} ${mentee.lastName}`}
                  style={{ 
                    width: '150px', 
                    height: '150px', 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    marginBottom: '16px'
                  }}
                />
              ) : (
                <Avatar size={150} icon={<UserOutlined />} style={{ marginBottom: '16px' }} />
              )}
              
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Mentee
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} sm={16} md={18}>
            <div style={{ marginBottom: '16px' }}>
              <Title level={1} style={{ margin: 0, marginBottom: '8px' }}>
                {mentee.firstName} {mentee.lastName}
              </Title>
              {mentee.title && (
                <Text type="secondary" style={{ fontSize: '18px', marginBottom: '16px', display: 'block' }}>
                  {mentee.title}
                </Text>
              )}
              
              {mentee.bio && !isCompany && (
                <Paragraph style={{ fontSize: '16px', marginBottom: '16px' }}>
                  {mentee.bio}
                </Paragraph>
              )}
            </div>

            <Row gutter={[16, 16]}>

              {mentee.experienceYears && (
                <Col xs={24} sm={12}>
                  <Space>
                    <ClockCircleOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentee.experienceYears} yıl deneyim</Text>
                  </Space>
                </Col>
              )}

              {mentee.category && (
                <Col xs={24} sm={12}>
                  <Space>
                    <CodeOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Tag color="blue">{mentee.category}</Tag>
                  </Space>
                </Col>
              )}
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              {mentee.email && (
                <Col xs={24} sm={12}>
                  <Space>
                    <MailOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentee.email}</Text>
                  </Space>
                </Col>
              )}

              {mentee.phone && (
                <Col xs={24} sm={12}>
                  <Space>
                    <PhoneOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentee.phone}</Text>
                  </Space>
                </Col>
              )}

              {mentee.website && (
                <Col xs={24} sm={12}>
                  <Space>
                    <GlobalOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentee.website} target="_blank" rel="noopener noreferrer">
                      {mentee.website}
                    </a>
                  </Space>
                </Col>
              )}

              {mentee.linkedIn && (
                <Col xs={24} sm={12}>
                  <Space>
                    <LinkedinOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentee.linkedIn} target="_blank" rel="noopener noreferrer">
                      LinkedIn Profili
                    </a>
                  </Space>
                </Col>
              )}

              {mentee.github && (
                <Col xs={24} sm={12}>
                  <Space>
                    <GithubOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentee.github} target="_blank" rel="noopener noreferrer">
                      GitHub Profili
                    </a>
                  </Space>
                </Col>
              )}

              {mentee.twitter && (
                <Col xs={24} sm={12}>
                  <Space>
                    <TwitterOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentee.twitter} target="_blank" rel="noopener noreferrer">
                      Twitter Profili
                    </a>
                  </Space>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderSkills = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" title="Yetkinlikler" style={{ marginBottom: '24px' }}>
        <Spin spinning={skillsLoading}>
          {mentee.skills && mentee.skills.length > 0 ? (
            <Row gutter={[8, 8]}>
              {mentee.skills.map((skill, index) => (
                <Col key={index}>
                  <Tag color="blue" style={{ margin: '4px' }}>
                    {skill.skillName}
                    {skill.skillLevel && ` (${skill.skillLevel})`}
                    {skill.experienceYears && ` - ${skill.experienceYears} yıl`}
                  </Tag>
                </Col>
              ))}
            </Row>
          ) : !skillsLoading && (
            <Text type="secondary">Henüz yetkinlik bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderEducations = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" title="Eğitim Bilgileri" style={{ marginBottom: '24px' }}>
        <Spin spinning={educationsLoading}>
          {mentee.educations && mentee.educations.length > 0 ? (
            <Timeline>
              {mentee.educations.map((education, index) => (
                <Timeline.Item key={index}>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {education.schoolName}
                    </Title>
                    <Text type="secondary">
                      {education.department}
                      {education.degree && ` - ${education.degree}`}
                    </Text>
                    <br />
                    <Text type="secondary">
                      {dayjs(education.startDate).format('YYYY')} - {education.endDate ? dayjs(education.endDate).format('YYYY') : 'Devam ediyor'}
                    </Text>
                    {education.description && (
                      <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                        {education.description}
                      </Paragraph>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : !educationsLoading && (
            <Text type="secondary">Henüz eğitim bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderWorkExperiences = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" title="İş Deneyimi" style={{ marginBottom: '24px' }}>
        <Spin spinning={workExperiencesLoading}>
          {mentee.workExperiences && mentee.workExperiences.length > 0 ? (
            <Timeline>
              {mentee.workExperiences.map((work, index) => (
                <Timeline.Item key={index}>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {work.position} - {work.companyName}
                    </Title>
                    <Text type="secondary">
                      {dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}
                    </Text>
                    {work.description && (
                      <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                        {work.description}
                      </Paragraph>
                    )}
                    {work.achievements && (
                      <div style={{ marginTop: '8px' }}>
                        <Text strong>Başarılar:</Text>
                        <Paragraph style={{ marginTop: '4px', marginBottom: 0 }}>
                          {work.achievements}
                        </Paragraph>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : !workExperiencesLoading && (
            <Text type="secondary">Henüz iş deneyimi bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderProjects = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" title="Projeler" style={{ marginBottom: '24px' }}>
        <Spin spinning={projectsLoading}>
          {mentee.projects && mentee.projects.length > 0 ? (
            <Row gutter={[16, 16]}>
              {mentee.projects.map((project, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small" hoverable>
                    <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                      {project.projectName}
                    </Title>
                    {project.projectDescription && (
                      <Paragraph style={{ marginBottom: '8px' }}>
                        {project.projectDescription.length > 100 
                          ? `${project.projectDescription.substring(0, 100)}...` 
                          : project.projectDescription}
                      </Paragraph>
                    )}
                    {project.technologies && (
                      <div style={{ marginBottom: '8px' }}>
                        {project.technologies.split(',').map((tech: string, i: number) => (
                          <Tag key={i} color="green" style={{ margin: '2px' }}>
                            {tech.trim()}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {project.githubUrl && (
                        <Button size="small" icon={<GithubOutlined />} href={project.githubUrl} target="_blank">
                          GitHub
                        </Button>
                      )}
                      {project.liveUrl && (
                        <Button size="small" icon={<GlobalOutlined />} href={project.liveUrl} target="_blank">
                          Canlı Demo
                        </Button>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : !projectsLoading && (
            <Text type="secondary">Henüz proje bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderCertificates = () => {
    if (!mentee) return null;

    return (
      <Card className="app-content-card" title="Sertifikalar" style={{ marginBottom: '24px' }}>
        <Spin spinning={certificatesLoading}>
          {mentee.certificates && mentee.certificates.length > 0 ? (
            <Row gutter={[16, 16]}>
              {mentee.certificates.map((cert, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small" hoverable>
                    {cert.certificateImage && (
                      <Image
                        src={`data:image/jpeg;base64,${cert.certificateImage}`}
                        alt={cert.certificateName}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', marginBottom: '8px' }}
                      />
                    )}
                    <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                      {cert.certificateName}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {cert.issuingOrganization}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {dayjs(cert.issueDate).format('MMM YYYY')}
                      {cert.expiryDate && ` - ${dayjs(cert.expiryDate).format('MMM YYYY')}`}
                    </Text>
                    {cert.credentialUrl && (
                      <div style={{ marginTop: '8px' }}>
                        <Button size="small" href={cert.credentialUrl} target="_blank">
                          Doğrula
                        </Button>
                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : !certificatesLoading && (
            <Text type="secondary">Henüz sertifika bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mentee && !loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <UserOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '16px' }} />
        <Title level={3} type="secondary">Mentee bulunamadı</Title>
        <Button onClick={() => navigate(menteeListPath)}>
          <ArrowLeftOutlined /> Geri Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-container">
        <div style={{ marginBottom: '24px' }}>
          <Button onClick={() => navigate(menteeListPath)} icon={<ArrowLeftOutlined />}>
            Geri Dön
          </Button>
        </div>

        {renderHeader()}
        {renderSkills()}
        {renderEducations()}
        {renderWorkExperiences()}
        {renderProjects()}
        {renderCertificates()}
      </div>
    </div>
  );
};

export default MenteeDetail;
